<template>
  <div class="follow-along-session">
    <div class="follow-along-session__inner container">
      <div
        class="follow-along-session__top-progress d-flex mb-1 align-items-center"
      >
        <v-ons-progress-bar
          class="flex-grow-1"
          :value="progress"
          secondary="100"
        />
      </div>

      <div class="follow-along-session__content bg-white">
        <transition name="fade-out-left" mode="out-in">
          <div
            :key="activeItemTransitionKey"
            class="follow-along-session__active-item"
          >
            <group-header
              :group="activeGroup"
              :completed-rounds="activeRound"
              :hide-total-rounds="activeGroup.is_amrap"
              @captureClicked="
                startIndex =>
                  $emit('captureClicked', {
                    groupUid: activeGroup.uid,
                    itemUid: null,
                    startIndex
                  })
              "
            >
            </group-header>

            <template
              v-if="
                activeExercise &&
                  (isPresent(activeExercise.banner) ||
                    isPresent(activeExercise.video))
              "
            >
              <hero
                :image="activeExercise.banner"
                :video="activeExercise.video"
                :styleOptions="{
                  heightRatio: 120,
                  heightRatioUnit: 'percent',
                  baseHeight: 100
                }"
                :videoOptions="{
                  autoplay: true,
                  muted: true,
                  loop: true,
                  cover: true
                }"
              />
            </template>

            <div v-if="activeItem">
              <list-item
                class="group-exercise-item"
                @click="() => !activeItem.is_rest && $router.push('#exercise')"
              >
                <h2 class="my-0">
                  <div class="group-exercise-item__title --no-wrap">
                    {{ activeItem.is_rest ? "Rest" : activeExercise.title }}
                  </div>
                </h2>

                <group-item-details
                  class="group-exercise-item__details font-weight-bold"
                  :item="activeItem"
                />

                <template slot="right">
                  <template v-if="!activeGroup.is_amrap && !activeItem.is_rest">
                    <form-capture-button
                      class="ml-1"
                      :capture-uid="activeItem.uid"
                      @capture="
                        startIndex =>
                          $emit('captureClicked', {
                            groupUid: activeGroup.uid,
                            itemUid: activeItem.uid,
                            startIndex
                          })
                      "
                    />
                  </template>
                </template>
                <template slot="row" v-if="activeItem.notes">
                  <span class="v-list-item__subtitle">
                    {{ activeItem.notes }}
                  </span>
                </template>
              </list-item>
            </div>

            <template
              v-if="activeGroup.is_rest || (activeItem && activeItem.is_rest)"
            >
              <div
                class="follow-along-session__progress"
                :style="{ transform: `scaleX(${timerProgress})` }"
              />
            </template>
          </div>
        </transition>
      </div>
    </div>

    <v-modal v-if="activeExercise">
      <template slot="#exercise">
        <span>
          <exercise-template
            :id="activeExercise.id"
            :title="activeExercise.title"
            :description="activeExercise.description"
            :banner="activeExercise.banner"
            :video="activeExercise.video"
            hide-back
          />
        </span>
      </template>
    </v-modal>
  </div>
</template>

<script>
import { mapMutations } from "vuex"

import { isPresent } from "@/lib/utils"
import { getGroupItemTime } from "@/lib/workout-utils"

import Hero from "@/components/Hero"
import GroupHeader from "@/components/workout/GroupHeader"

export default {
  components: {
    Hero,
    GroupHeader,
    ExerciseTemplate: () =>
      import("@/components/page-templates/ExerciseTemplate"),
    ListItem: () => import("@/components/ListItem"),
    GroupItemDetails: () => import("@/components/workout/GroupItemDetails"),
    FormCaptureButton: () => import("@/components/workout/FormCaptureButton")
  },

  props: {
    session: { type: Object, required: true },
    workoutId: { type: Number, required: true },
    activeGroup: { type: Object, required: true },
    nextGroup: { type: Object, default: null },
    activeItem: { type: Object, default: null },
    activeRound: { type: Number, required: true },
    activeSet: { type: Number, required: true },
    progress: { type: Number, required: true },
    timerProgress: { type: Number, default: 0 }
  },

  methods: {
    isPresent,

    ...mapMutations("workouts", ["setIsFollowAlongVisible"])
  },

  computed: {
    activeItemTransitionKey() {
      return `${this.activeItem ? this.activeItem.id : this.activeGroup.id}-${
        this.activeRound
      }-${this.activeSet}`
    },

    activeExercise() {
      if (!this.activeItem) return null
      if (this.activeItem.is_rest) return null

      return this.activeItem.exercise
    },

    activeItemTime() {
      return getGroupItemTime(this.activeItem)
    }
  },

  mounted() {
    this.setIsFollowAlongVisible(true)
  },

  beforeDestroy() {
    this.setIsFollowAlongVisible(false)
  }
}
</script>

<style lang="scss">
@import "@/assets/styles/variables";
$page-black-color: #1f1f21;

.follow-along-session {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  background-color: $color-ui--white;
  color: $page-black-color;
  z-index: 90;
  height: 100%;
  padding-top: $tabbar--height;
  padding-top: calc(#{$tabbar--height} + env(safe-area-inset-top));
}

.follow-along-session__content {
  padding-bottom: 120px; // cta with timer
  flex-grow: 1;
}

.follow-along-session__inner {
  overflow-y: auto;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.follow-along-session__active-item {
  min-height: 100%;
  display: flex;
  flex-direction: column;
}

.follow-along-session__up-next {
  h6 {
    margin-bottom: -$size--1;
  }
}

.follow-along-session__progress {
  background: $color-ui--grey-30;
  transform: scaleX(0);
  transform-origin: 0 0;
  transition: transform 0.25s linear;
  flex-grow: 1;
}

.follow-along-session__top-progress {
  .progress-bar--material {
    height: 2px;
    border-radius: 0;
    background-color: $color-ui--grey-10;
  }

  .progress-bar__primary {
    background-color: var(--brand-color-hex);
  }
}
</style>
